import styled from '@emotion/styled';
import { Prismic, PrismicLink } from '@landsbankinn/prismic';
import { responseFont, typography } from '@landsbankinn/ui';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useState, useEffect } from 'react';
import { use100vh } from 'react-div-100vh';
import { animated, useSpring } from 'react-spring';
import { SwooshLink } from 'components/navigation/SwooshLink';
import { urlResolver } from 'lib/linkResolver';
import { OnlinebankLinks } from './OnlinebankLinks';

const Host = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  /* height: calc(100vh - 80px); */
  overflow: hidden;
  z-index: 20;
  padding-bottom: 20px;
`;

const Background = styled(animated.div)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff;
`;

const Main = styled(animated.div)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const Panels = styled(animated.div)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-y: auto;
`;

const Panel = styled(animated.div)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 40px;

  display: flex;
  flex-direction: column;
`;

const Level1Item = styled(animated.li)`
  ${typography.h1}
  font-size: 27px;
  @media (min-width: 414px) {
    ${responseFont(30, 50)}
  }
  line-height: 1.8em;
  cursor: pointer;
  > svg {
    position: absolute;
    top: 50%;
    right: -5px;
    width: 9px;
    margin-top: -7px;
  }
`;

const Level2Item = styled(animated.li)`
  ${typography.h2}
  font-size: 29px;
  line-height: 2.1em;
  border-bottom: 1px solid rgba(29, 61, 90, 0.1);
  white-space: nowrap;
  cursor: pointer;
  > svg {
    position: absolute;
    top: 50%;
    right: 0;
    width: 6px;
    margin-top: -4px;
  }
`;

const Level2Back = styled(animated.li)`
  ${typography.h1}
  font-size: 32px;
  position: relative;
  margin-bottom: 16px;
  cursor: pointer;
  > svg {
    position: absolute;
    top: 50%;
    left: -32px;
    width: 9px;
    margin-top: -9px;
    transform: rotateZ(180deg);
  }
`;

const Level3Item = styled(animated.li)`
  ${typography.h3}
  font-size: 24px;
  line-height: 1.4em;
  border-bottom: 1px solid rgba(29, 61, 90, 1);
  cursor: pointer;
  padding: 12px 0;
`;

const Level3Top = styled(animated.li)`
  font-family: ${(props) => props.theme.font.familySerif};
`;

const A = styled.a`
  display: block;
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-right: -30px;
  margin-top: 30px;
  > ${SwooshLink} {
    margin-left: 0;
  }
`;

export const Arrow = () => (
  <svg viewBox="0 0 9 17" version="1.1">
    <g
      transform="translate(-479.000000, -213.000000)"
      stroke="#1E3C5A"
      strokeWidth="3"
    >
      <polyline points="481 214 486 221.5 481 229" fill="white" />
    </g>
  </svg>
);

const bottomLinkColors = ['#FF8316', '#409DB0', '#1E3D5A'];

export function MobileSheet({ open, items, bottomLinks }: any) {
  const height = use100vh();
  const [level, setLevel] = useState(0);
  const [visible, setVisible] = useState(false);
  const [level1, setLevel1] = useState<any>(null);
  const [level2, setLevel2] = useState<any>(null);
  const router = useRouter();

  const style = useSpring({
    // config: { mass: 1, tension: 280, friction: 50 },
    y: open ? '0%' : '-101%',
    onRest(e) {
      if (parseInt(e.value.y, 10) < -100) {
        setVisible(false);
      }
    },
    delay: open ? 0 : 200,
  });

  const main = useSpring({
    opacity: Number(open),
    delay: open ? 100 : 0,
  });

  const cfg = {
    mass: 1,
    tension: 170,
    friction: 26,
  };

  const style0 = useSpring({
    opacity: level === 0 ? 1 : 0,
    x: `${level * -100}%`,
    i: level === 0 ? 0 : 1,
    config: cfg,
  });

  const style1 = useSpring({
    opacity: level === 1 ? 1 : 0,
    x: level > 1 ? '-100%' : '0%',
    i: level === 1 ? 0 : level === 2 ? 20 : -2,
    config: cfg,
  });

  const style2 = useSpring({
    opacity: level === 2 ? 1 : 0,
    i: level === 2 ? 0 : -2,
    config: cfg,
  });

  useEffect(() => {
    if (open) {
      setVisible(true);
    }
  }, [open]);

  return (
    <Host hidden={!visible} style={{ height: (height ?? 0) - 80 }}>
      <Background style={style} />
      <Main style={main}>
        <Panels>
          <Panel style={{ ...style2, marginLeft: 10 }} id="LEVEL_2">
            <ul>
              <Level3Top
                style={{
                  transform: style2.i.to(
                    (i) => `translateY(${-5 + (1 - i) * 5}px)`,
                  ),
                }}
              >
                {Prismic.text(level1?.navigation_title)}
              </Level3Top>
              <Level2Back onClick={() => setLevel(1)}>
                <animated.span
                  style={{
                    display: 'inline-block',
                    transform: style2.i.to((i) => `translateX(${i * 20}px)`),
                  }}
                >
                  {level2?.primary?.link_text ||
                    Prismic.text(level2?.primary?.link?.page_title)}
                </animated.span>
                <Arrow />
              </Level2Back>
              {level2?.fields?.map((item: any, index: number) => {
                const title =
                  item?.third_level_link_text ||
                  Prismic.text(item?.third_level_link?.page_title);
                return (
                  <Level3Item
                    style={{
                      transform: style2.i.to(
                        (i) => `translateX(${i * 1 * 20}px)`,
                      ),
                    }}
                    key={index}
                  >
                    <PrismicLink value={item?.third_level_link}>
                      <A>{title}</A>
                    </PrismicLink>
                  </Level3Item>
                );
              })}
            </ul>
          </Panel>
          <Panel
            style={{
              ...style1,
              pointerEvents: level === 1 ? 'auto' : 'none',
              marginLeft: 10,
            }}
            id="LEVEL_1"
          >
            <ul>
              <Level2Back onClick={() => setLevel(0)}>
                <animated.span
                  style={{
                    display: 'inline-block',
                    transform: style0.i.to((i) =>
                      level === 2
                        ? `translateX(${i * -300}px)`
                        : `translateX(${-20 + i * 20}px)`,
                    ),
                  }}
                >
                  {Prismic.text(level1?.navigation_title)}
                </animated.span>
                <Arrow />
              </Level2Back>
              {level1?.navigation_item?.body?.map(
                (item: any, index: number) => {
                  const title =
                    item?.primary?.link_text ||
                    Prismic.text(item?.primary?.link?.page_title);
                  return (
                    <Level2Item
                      onClick={() => {
                        setLevel2(item);
                        setLevel(2);
                      }}
                      key={index}
                      style={{
                        transform: style1.i.to(
                          (i) => `translateX(${i * 10}px)`,
                        ),
                      }}
                    >
                      {title}
                      <Arrow />
                    </Level2Item>
                  );
                },
              )}
            </ul>
          </Panel>
          <Panel
            style={{
              ...style0,
              pointerEvents: level === 0 ? 'auto' : 'none',
            }}
            id="LEVEL_0"
          >
            <ul style={{ flex: 1 }}>
              {items?.map((item: any, index: number) => {
                const link = Prismic.link(
                  urlResolver,
                  item?.navigation_item?.link,
                  item?.navigation_item?.title,
                );
                const isMega = item?.navigation_item?.visible !== false;
                return (
                  <Level1Item
                    key={index}
                    onClick={() => {
                      if (!isMega) {
                        return router.push(link.href, link.as);
                      }
                      setLevel1(item);
                      setLevel(1);
                    }}
                    style={{
                      transform: style0.i.to(
                        (i) =>
                          `translateX(${i * (items?.length - index) * 50}px)`,
                      ),
                    }}
                  >
                    {Prismic.text(item.navigation_title)}
                    {isMega && <Arrow />}
                  </Level1Item>
                );
              })}
              <OnlinebankLinks />
            </ul>
            <Bottom>
              {bottomLinks?.map((bottomLink: any, i: number) => {
                const color = bottomLinkColors[i];
                const text = Prismic.text(bottomLink.bottomlink_title);
                return (
                  <Link
                    href={
                      Prismic.link(urlResolver, bottomLink.bottomlink_link).href
                    }
                    passHref
                    key={`bottom-link-${i}`}
                  >
                    <SwooshLink color={color}>{text}</SwooshLink>
                  </Link>
                );
              })}
            </Bottom>
          </Panel>
        </Panels>
      </Main>
    </Host>
  );
}
